import type { WidgetDataContract } from "@nexus/widget-sdk";

/**
 * `protection-overview-campaign-events` v1 — one campaign's protected events
 * for one published Protection Overview report edition.
 *
 * Unlike `protection-overview-tenant-campaigns` (scoped by a dashboard-owned
 * static `tenant_id`-derived `scope_key`), this dataset's `scope_key` is the
 * campaign id resolved from the `:campaignId` route param at render time —
 * this widget lives on one generic "campaign-detail" dashboard template
 * shared by every campaign, not one dashboard seeded per campaign. See
 * `webui/app/src/lib/live/data-sources/reportSnapshots.ts` for the resolver.
 */
export const PROTECTION_OVERVIEW_CAMPAIGN_EVENTS_CONTRACT: WidgetDataContract = {
  name: "protection-overview-campaign-events",
  version: 1,
};

export interface ProtectionOverviewCampaignHeader {
  id: string;
  label: string;
  start: string;
  end: string;
  /** The tenant this campaign belongs to, when known — used to climb the drill-down breadcrumb. */
  tenant?: { id: number; label: string };
}

export interface ProtectionOverviewCampaignEvent {
  id: string;
  label: string;
  type: string;
  channel: string;
  start: string;
  end: string;
  popularity: number;
  integrity: number;
  hasReport: boolean;
  /** Competition, e.g. "UEFA C1 2026-2027" (optional). */
  collectionGroup?: string | null;
  /** Round or stage within the competition, e.g. "Phase 5" (optional). */
  collection?: string | null;
  /** Image reference of the competition logo (see `WidgetAssetsContext`). */
  logo?: string | null;
}

export interface ProtectionOverviewCampaignEventsFreshness {
  generatedAt: string;
  dataThrough: string;
  /** "demo", "clickhouse", or a named upstream feed. */
  source: string;
  isEstimated: boolean;
}

export interface ProtectionOverviewCampaignEventsData {
  campaign: ProtectionOverviewCampaignHeader;
  events: ProtectionOverviewCampaignEvent[];
  freshness: ProtectionOverviewCampaignEventsFreshness;
}
